import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { AuthService } from '@/features/auth/services/authService';
import { useTranslation } from 'react-i18next';

/**
 * Login page that redirects to WorkOS authentication
 */
export function LoginPage() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('common');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const errorParam = searchParams.get('error');
    const emailParam = searchParams.get('email');

    // Handle error cases
    if (errorParam === 'adult_not_found') {
      setErrorMessage(t('login.errors.accessDenied', { email: emailParam || 'your account' }));
      setShowError(true);
      return;
    }

    if (errorParam) {
      setErrorMessage(t('login.errors.authFailed'));
      setShowError(true);
      return;
    }

    // Automatically redirect to WorkOS login
    AuthService.login();
  }, [searchParams]);

  if (showError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white py-8 px-6 shadow rounded-lg">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('login.accessUnavailable')}</h2>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <button
                onClick={() => AuthService.login()}
                className="w-full bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors mb-6"
              >
                {t('login.tryAgain')}
              </button>

              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 text-center">{t('login.supportContact')}</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center justify-center">
                    <svg className="h-4 w-4 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    <a href="tel:+1-506-384-7874" className="text-blue-600 hover:text-blue-500 underline">
                      (506) 384-7874
                    </a>
                  </div>
                  <div className="flex items-center justify-center">
                    <svg className="h-4 w-4 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span>86 Rue Ste Therese, Dieppe, NB E1A 1S7</span>
                  </div>
                  <div className="flex items-center justify-center">
                    <svg className="h-4 w-4 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{t('login.hours')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">{t('login.title')}</h2>
          <p className="mt-2 text-sm text-gray-600">{t('login.redirecting')}</p>
        </div>

        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>

        <div className="text-center">
          <button onClick={() => AuthService.login()} className="text-blue-600 hover:text-blue-500 text-sm">
            {t('login.manualRedirect')}
          </button>
        </div>
      </div>
    </div>
  );
}
